import React from 'react';
import { ArrowRight } from 'lucide-react';
import { projectCategories } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { navigateTo } from '../utils/navigation';

const Projects = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [sectionRef, sectionVisible] = useScrollAnimation(0.15);

  return (
    <section id="projects" className="py-24 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-20">
        <div
          ref={headerRef}
          className={`text-center mb-14 scroll-fade-in ${headerVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-blue-400 bg-clip-text text-transparent">
            Project Categories
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Explore our completed work across automation, residential, enterprise, manufacturing, and institutional environments.
          </p>
        </div>

        <div
          ref={sectionRef}
          className={`space-y-5 scroll-fade-in ${sectionVisible ? 'visible' : ''}`}
        >
          {projectCategories.map((category, index) => (
            <button
              key={category.slug}
              type="button"
              onClick={() => navigateTo(`/projects/${category.slug}`)}
              className="group relative block w-full overflow-hidden rounded-[28px] border border-white/10 text-left shadow-[0_20px_70px_rgba(2,6,23,0.28)]"
            >
              <div className="absolute inset-0">
                <img
                  src={category.image}
                  alt={category.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(37,99,235,0.20),transparent_30%),linear-gradient(180deg,rgba(2,6,23,0.08)_0%,rgba(2,6,23,0.40)_36%,rgba(2,6,23,0.94)_100%)]" />
                <div
                  className="absolute inset-0 opacity-[0.12]"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(59,130,246,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.18) 1px, transparent 1px)',
                    backgroundSize: '48px 48px'
                  }}
                />
              </div>

              <div className="relative flex min-h-[260px] flex-col justify-between p-6 md:min-h-[340px] md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-full border border-blue-500/25 bg-black/20 px-4 py-2 backdrop-blur-sm">
                    <span className="text-[10px] uppercase tracking-[0.26em] text-blue-300/90">
                      {category.eyebrow}
                    </span>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/20 text-white/75 backdrop-blur-sm">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                </div>

                <div className="max-w-2xl">
                  <h3 className="text-2xl md:text-4xl font-semibold leading-tight text-white">
                    {category.title}
                  </h3>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-gray-300">
                    {category.summary}
                  </p>

                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-blue-300 transition-colors duration-300 group-hover:text-white">
                    View projects
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
